import { sessionStorage } from '../utils/namespaced-storage';
import { fetchJson } from '../utils/fetch';
import { window } from '../env/browser';
import { API_BASE_URL } from '../constants';

const SSO_JWT_KEY = 'sso_access_token';
const SSO_CODE_PARAM = 'sso_code';
const PREFERRED_MERCHANT_ACCOUNT_KEY = 'preferred_merchant_account_id';

function preferredMerchantKey(accountId: string): string {
  return `${accountId}:preferred_merchant_id`;
}

export interface SsoAuth {
  ssoMode: boolean;
  accessToken?: string;
  merchantProfileId?: string;
}

interface SsoTokenAttributes {
  access_token: string;
  merchant_profile_id?: string;
}

interface SsoTokenResponse {
  data: {
    attributes: SsoTokenAttributes;
  };
}

function isJwtValid(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) {
      return false;
    }
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { exp?: number };
    if (typeof payload.exp !== 'number') {
      return false;
    }
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function stripSsoCodeFromUrl(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(SSO_CODE_PARAM)) {
    return;
  }
  url.searchParams.delete(SSO_CODE_PARAM);
  // Keep behavior consistent with src/utils/url.ts setQueryStringParameter.
  window.history.replaceState(null, '', url.toString());
}

export async function resolveSsoAuth(): Promise<SsoAuth> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get(SSO_CODE_PARAM);

  if (code) {
    try {
      // fetchJson already sets accept + content-type; don't re-pass Content-Type
      // (different casing would duplicate the header key in the request).
      const { json } = await fetchJson<SsoTokenResponse>(
        `${API_BASE_URL}/v3/sso_tokens`,
        {
          method: 'POST',
          body: JSON.stringify({ code }),
        }
      );
      const attrs = json.data.attributes;
      sessionStorage.setItem(SSO_JWT_KEY, attrs.access_token);
      stripSsoCodeFromUrl();
      return {
        ssoMode: true,
        accessToken: attrs.access_token,
        merchantProfileId: attrs.merchant_profile_id,
      };
    } catch {
      stripSsoCodeFromUrl();
      clearSsoToken();
    }
  }

  const stored = sessionStorage.getItem(SSO_JWT_KEY);
  if (stored && isJwtValid(stored)) {
    return { ssoMode: true, accessToken: stored };
  }

  return { ssoMode: false };
}

export function persistSsoToken(token: string): void {
  sessionStorage.setItem(SSO_JWT_KEY, token);
}

export function setPreferredMerchantId(
  accountId: string,
  merchantId: string
): void {
  sessionStorage.setItem(preferredMerchantKey(accountId), merchantId);
  sessionStorage.setItem(PREFERRED_MERCHANT_ACCOUNT_KEY, accountId);
}

export function getPreferredMerchantId(accountId: string): string | null {
  return sessionStorage.getItem(preferredMerchantKey(accountId));
}

export function clearPreferredMerchantId(accountId?: string): void {
  const id =
    accountId ?? sessionStorage.getItem(PREFERRED_MERCHANT_ACCOUNT_KEY);
  if (id) {
    sessionStorage.removeItem(preferredMerchantKey(id));
  }
  if (
    !accountId ||
    id === sessionStorage.getItem(PREFERRED_MERCHANT_ACCOUNT_KEY)
  ) {
    sessionStorage.removeItem(PREFERRED_MERCHANT_ACCOUNT_KEY);
  }
}

export function clearSsoToken(): void {
  sessionStorage.removeItem(SSO_JWT_KEY);
  clearPreferredMerchantId();
}
